import { find } from "./dom";

/**
 * Carries the photograph a visitor clicks into the next page's main photograph.
 *
 * <p>The cross-fade between pages is CSS alone, the @view-transition rule in base.css. What CSS
 * cannot tell is which card was clicked. The next page's main photograph is named page-photo in
 * CSS, and a picture only morphs when the page being left holds the same name, so this module
 * gives the name to the clicked card's photograph, and to nothing else, as the page is swapped
 * out.</p>
 *
 * <p>Naming every card up front cannot work: a name must be unique on a page, and every card
 * leads somewhere else. Nor may the page's own main photograph keep its name on the way out.
 * Leaving one accommodation for another through the cards under it would then morph one room's
 * photograph into another's, and a name on both it and the clicked card cancels the transition
 * altogether.</p>
 *
 * <p>A browser without pageswap or without cross-document view transitions never fires the event
 * with a transition, and the page navigates as it always has.</p>
 */

const NAME = "page-photo";

// The page's main photograph, which base.css names page-photo. ImageModel lets exactly one image
// per page be the LCP image, and that is what keeps the name unique.
const MAIN_PHOTO = 'img[fetchpriority="high"]';

// The cards whose photograph and links all lead to the same page. A link in a card's text, such as
// its heading, carries the card's photograph; a link outside a card carries only its own image.
const CARD = ".room-card, .feature, .feature-post, .post-row, .post";

let clicked: HTMLAnchorElement | null = null;
let renamed: HTMLElement[] = [];

/** Names the clicked card's photograph, and unnames the page's own, when the page is swapped out. */
export function initPageTransition(): void {
  if (!("onpageswap" in window)) {
    return;
  }

  // Remembered from the click rather than looked up from the destination: several links on a page
  // lead to the same address, and only the one the visitor used says which photograph to carry.
  document.addEventListener("click", (event) => {
    clicked = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
  }, { capture: true });

  window.addEventListener("pageswap", (event) => {
    // A page restored from the back/forward cache still carries the names of its last departure.
    restore();
    if (event.viewTransition === null) {
      return;
    }

    const main = find<HTMLElement>(MAIN_PHOTO);
    if (main !== null) {
      rename(main, "none");
    }
    const photo = clickedPhoto(event.activation?.entry.url ?? null);
    if (photo !== null) {
      rename(photo, NAME);
    }
  });
}

/** The photograph that belongs to the link the visitor clicked, if that link is where the page is going. */
function clickedPhoto(destination: string | null): HTMLElement | null {
  if (clicked === null || destination === null || !sameDocument(clicked.href, destination)) {
    return null;
  }
  return clicked.querySelector<HTMLElement>("img") ?? clicked.closest(CARD)?.querySelector<HTMLElement>("img") ?? null;
}

/** Sets an element's transition name inline and remembers it, so the next departure can undo it. */
function rename(element: HTMLElement, name: string): void {
  element.style.setProperty("view-transition-name", name);
  renamed.push(element);
}

/** Hands every renamed element back to the stylesheet. */
function restore(): void {
  for (const element of renamed) {
    element.style.removeProperty("view-transition-name");
  }
  renamed = [];
}

/** Tells whether two addresses are the same document, ignoring the fragment. */
function sameDocument(a: string, b: string): boolean {
  const left = new URL(a);
  const right = new URL(b);
  left.hash = "";
  right.hash = "";
  return left.href === right.href;
}
